import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useMemo, useRef } from "react";
import { flushSync } from "react-dom";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";

import { useAppSelector } from "~/app/store";
import { WorkstationChangesToolbar } from "~/features/preview/workstation/components/WorkstationChangesToolbar";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import { allTemperatureZones } from "~/lib/tempZone";
import { selectUserIsAdmin } from "~/redux/selectors/authSelectors";
import { selectDropdownWorkstation } from "~/redux/selectors/workstationConfigSelectors";
import { useGetAutostoreGridQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import {
  useGetWorkstationPickingConfigurationQuery,
  useGetWorkstationsQuery
} from "~/redux/warehouse/workstation.hooks";
import {
  TemperatureZone,
  UpdateWorkstation,
  WorkstationSummaryDto
} from "~/types/api";

export type WorkstationSettingsFormValues = UpdateWorkstation & {
  temperatureZones: TemperatureZone[];
};

export type WorkstationConfigFormReturn =
  UseFormReturn<WorkstationSettingsFormValues> & {
    pickingTempZoneOptions: TemperatureZone[];
    canEditPickingTempZones: boolean;
  };

const EMPTY_FORM_VALUES: WorkstationSettingsFormValues = {
  deviceId: "",
  autostoreGridId: "",
  orientation: "",
  roles: [],
  multiPortEnabled: false,
  ports: [],
  totePlacements: [],
  workstationId: "",
  mode: "",
  pickableOrderTypes: [],
  temperatureZones: []
};

/**
 * RTK/Immer may freeze workstation rows from the cache. react-hook-form mutates
 * nested values in place (ports[], totePlacements[].totePosition, etc.), so defaults
 * must be deep-cloned mutable copies.
 */
function mutableWorkstationFormValues(
  workstation: WorkstationSummaryDto | undefined,
  temperatureZones: TemperatureZone[] = []
): WorkstationSettingsFormValues {
  if (!workstation?.id) {
    return structuredClone(EMPTY_FORM_VALUES);
  }
  return structuredClone({
    deviceId: workstation.deviceId || "",
    autostoreGridId: workstation.autostoreGridId || "",
    orientation: workstation.orientation || "",
    roles: workstation.roles ? [...workstation.roles] : [],
    multiPortEnabled: workstation.multiPortEnabled || false,
    ports: workstation.ports ? [...workstation.ports] : [],
    totePlacements: workstation.totePlacements
      ? [...workstation.totePlacements]
      : [],
    workstationId: workstation.id,
    mode: workstation.mode || "",
    pickableOrderTypes: workstation.pickableOrderTypes
      ? [...workstation.pickableOrderTypes]
      : [],
    temperatureZones: [...temperatureZones]
  });
}

export const useWorkstationConfigForm = ({
  viewTitle,
  onExitNameEdit,
  isEditingName = false
}: {
  viewTitle: ViewNameTranslation | undefined;
  onExitNameEdit?: () => void;
  isEditingName?: boolean;
}): WorkstationConfigFormReturn => {
  const { setToolbar } = useNavbar({ viewTitle });
  useGetWorkstationsQuery();
  const workstation = useAppSelector(selectDropdownWorkstation);
  const isAdmin = useAppSelector(selectUserIsAdmin);
  const loadedPickingConfigForWsRef = useRef<string | null>(null);
  // Prevents a stale toolbar effect from remounting Cancel-only after save/cancel.
  const suppressToolbarRef = useRef(false);

  const gridId = workstation?.autostoreGridId;
  const { data: autostoreGrid } = useGetAutostoreGridQuery(gridId ?? skipToken);
  const gridTempZones = autostoreGrid?.temperatureZones ?? [];
  const isMultiTempGrid = gridTempZones.length > 1;
  const pickingTempZoneOptions: TemperatureZone[] =
    gridTempZones.length > 0 ? gridTempZones : allTemperatureZones;
  const canEditPickingTempZones = isMultiTempGrid;

  const pickingConfigArgs =
    gridId && workstation?.id
      ? { autostoreGridId: gridId, workstationId: workstation.id }
      : skipToken;

  const { data: pickingConfig, isSuccess: isPickingConfigSuccess } =
    useGetWorkstationPickingConfigurationQuery(pickingConfigArgs, {
      refetchOnMountOrArgChange: true
    });

  const defaultValues = useMemo(
    (): WorkstationSettingsFormValues =>
      mutableWorkstationFormValues(workstation),
    [workstation]
  );

  const form = useForm<WorkstationSettingsFormValues>({ defaultValues });
  const {
    formState: { isDirty },
    reset
  } = form;

  // without flushSync + suppress, a stale dirty/edit effect can briefly remount
  // the toolbar again after dismiss (reset flips isDirty while isEditingName
  // is still catching up).
  const dismissToolbar = (nextValues?: WorkstationSettingsFormValues) => {
    suppressToolbarRef.current = true;
    flushSync(() => {
      onExitNameEdit?.();
      setToolbar(undefined);
    });
    if (nextValues) {
      reset(nextValues);
    } else {
      reset();
    }
    // Hold suppress through effects scheduled above, then resume normal mounts.
    queueMicrotask(() => {
      suppressToolbarRef.current = false;
    });
  };

  useEffect(() => {
    loadedPickingConfigForWsRef.current = null;
    reset(mutableWorkstationFormValues(workstation));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workstation?.id, reset]);

  useEffect(() => {
    if (!workstation?.id || !isPickingConfigSuccess) {
      return;
    }
    const loadedZones = pickingConfig?.configuration.temperatureZones;
    const loadedForWorkstationId = pickingConfig?.configuration.workstationId;
    if (loadedForWorkstationId !== workstation.id) {
      return;
    }
    if (loadedPickingConfigForWsRef.current === workstation.id) {
      return;
    }
    loadedPickingConfigForWsRef.current = workstation.id;
    reset(mutableWorkstationFormValues(workstation, [...(loadedZones ?? [])]));
  }, [
    workstation,
    isPickingConfigSuccess,
    pickingConfig?.configuration.temperatureZones,
    pickingConfig?.configuration.workstationId,
    reset
  ]);

  useEffect(() => {
    if (suppressToolbarRef.current) {
      setToolbar(undefined);
      return;
    }
    if (isAdmin && (isDirty || isEditingName)) {
      setToolbar(
        <FormProvider {...form}>
          <WorkstationChangesToolbar onDismissToolbar={dismissToolbar} />
        </FormProvider>
      );
    } else {
      setToolbar(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form/dismissToolbar omitted (unstable identity); toolbar mirrors dirty/edit state
  }, [isDirty, isEditingName, isAdmin, setToolbar, onExitNameEdit]);

  return {
    ...form,
    pickingTempZoneOptions,
    canEditPickingTempZones
  };
};
